import { useSnackbar } from 'notistack';
import { FilterEmployeesDTO, UpdateBenefitActivationDTO } from 'probonio-shared-ui/api';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogState } from '../../../component/dialog';
import { RowSelectionState } from '../../../component/table/useManualRowSelection';
import { useEmployeeIdList } from '../../userManagement/useEmployeeIdList';
import { useBenefitContext } from '../BenefitContext';
import { EmployeeFilter, mapEmployeeFilterToRequest } from '../employeeStatusList/employeeFilterState';
import { ActivationActionsDialog } from './ActivationActionsDialog';
import { PauseMutationDTO, useBulkPauseActivationsMutation } from './useBulkPauseActivationsMutation';
import { useBulkUpdateActivationsMutation } from './useBulkUpdateActivationsMutation';

interface Props {
  dialogState: DialogState;
  employeeFilter: EmployeeFilter;
  rowSelectionState: RowSelectionState;
}

export const BulkEditActivationsDialog: React.FC<Props> = ({ dialogState, employeeFilter, rowSelectionState }) => {
  const { t } = useTranslation('benefitsModule');
  const { enqueueSnackbar } = useSnackbar();
  const { benefit, customBenefit } = useBenefitContext();
  const [updateProgress, setUpdateProgress] = useState<number>();
  const [pauseProgress, setPauseProgress] = useState<number>();

  const selectedEmployeeIds = Object.keys(rowSelectionState.rowSelection).filter(id => rowSelectionState.rowSelection[id]);
  const mappedEmployeeFilter: FilterEmployeesDTO = {
    ...mapEmployeeFilterToRequest(employeeFilter),
    considerFutureActivations: true,
    activeBenefit: benefit,
    activeCustomBenefitId: customBenefit?.id,
    excludeEmployeeStatus: ['INACTIVE'],
  };

  const {
    data: allEmployeeIds,
    isLoading: allEmployeeIdsLoading,
    isError: allEmployeeIdsError,
  } = useEmployeeIdList(mappedEmployeeFilter, {
    enabled: rowSelectionState.allSelected && dialogState.isOpen,
  });
  const employeeIds = rowSelectionState.allSelected && allEmployeeIds?.employeeIds ? allEmployeeIds.employeeIds : selectedEmployeeIds;
  const employeeIdsError = rowSelectionState.allSelected && allEmployeeIdsError;
  const employeeIdsLoading = rowSelectionState.allSelected && allEmployeeIdsLoading;

  useEffect(() => {
    if (employeeIdsError) {
      enqueueSnackbar(t('bulkEditDialog.employeeListError'), { variant: 'error' });
    }
  }, [employeeIdsError, enqueueSnackbar, t]);

  const bulkUpdateMutation = useBulkUpdateActivationsMutation(employeeIds, setUpdateProgress, benefit, customBenefit?.id);
  const bulkPauseMutation = useBulkPauseActivationsMutation(employeeIds, setPauseProgress, benefit, customBenefit?.id);

  const handleDeactivate = useCallback((dto: UpdateBenefitActivationDTO) => bulkUpdateMutation.mutateAsync(dto), [bulkUpdateMutation]);

  const handlePause = useCallback((dto: PauseMutationDTO) => bulkPauseMutation.mutateAsync(dto), [bulkPauseMutation]);

  const handleUpdate = useCallback((dto: UpdateBenefitActivationDTO) => bulkUpdateMutation.mutateAsync(dto), [bulkUpdateMutation]);

  return (
    <ActivationActionsDialog
      dialogState={dialogState}
      translationPrefix="bulkEditDialog"
      isBulkMode
      onDeactivate={handleDeactivate}
      onPause={handlePause}
      onUpdate={handleUpdate}
      onSuccessReset={rowSelectionState.handleReset}
      updateProgress={updateProgress}
      pauseProgress={pauseProgress}
      disabled={employeeIdsLoading || employeeIdsError}
    />
  );
};
